import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { auditsApi } from '../api';
import { toast } from 'sonner';
import {
  Loader2,
  ArrowLeft,
  Save,
  FileText,
  Download,
  Building2,
  Plus,
  Trash2,
  Calendar,
  Users,
} from 'lucide-react';

const STATUS_CONFIG = {
  planned: { label: 'Planejada', color: 'badge-gray' },
  scheduled: { label: 'Agendada', color: 'badge-blue' },
  in_progress: { label: 'Em Andamento', color: 'badge-yellow' },
  completed: { label: 'Concluída', color: 'badge-green' },
  cancelled: { label: 'Cancelada', color: 'badge-red' },
};

const AUDIT_TYPES = [
  { value: 'internal', label: 'Interna' },
  { value: 'external', label: 'Externa' },
  { value: 'supplier', label: 'Fornecedor' },
  { value: 'certification', label: 'Certificação' },
  { value: 'surveillance', label: 'Manutenção' },
  { value: 'process', label: 'Processo' },
];

const MARKS = [
  { key: 'is_c', label: 'C', title: 'Conforme' },
  { key: 'is_p', label: 'P', title: 'Não Conforme' },
  { key: 'is_m', label: 'M', title: 'Melhorar' },
];

const resultToMarks = (result) => {
  if (result === 'conforming') return { is_c: true, is_p: false, is_m: false };
  if (result === 'non_conforming') return { is_c: false, is_p: true, is_m: false };
  if (result === 'improvement') return { is_c: false, is_p: false, is_m: true };
  return { is_c: false, is_p: false, is_m: false };
};

const AuditReport = () => {
  const { auditId } = useParams();
  const navigate = useNavigate();

  const [audit, setAudit] = useState(null);
  const [report, setReport] = useState(null);
  const [empresa, setEmpresa] = useState('');
  const [distributions, setDistributions] = useState([]);
  const [areaGroups, setAreaGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    loadAll();
  }, [auditId]);

  const buildAreaGroups = (items) => {
    const map = {};
    items.forEach((item) => {
      const sectors = item.sectors || [];
      if (sectors.length === 0) {
        const key = '__none__';
        if (!map[key]) {
          map[key] = { sector_id: null, sector_name: 'Sem área definida', sector_code: '', rows: [] };
        }
        map[key].rows.push({
          sequence: item.sequence,
          requirement: item.requirement || '',
          description: item.question,
          observation: '',
          result: null,
        });
        return;
      }
      sectors.forEach((sec) => {
        const key = sec.sector_id || `${sec.sector_name}|${sec.sector_code}`;
        if (!map[key]) {
          map[key] = {
            sector_id: sec.sector_id,
            sector_name: sec.sector_name || '',
            sector_code: sec.sector_code || '',
            rows: [],
          };
        }
        map[key].rows.push({
          sequence: item.sequence,
          requirement: item.requirement || '',
          description: item.question,
          observation: sec.observation || '',
          result: sec.result || null,
        });
      });
    });
    return Object.values(map)
      .map((g) => ({
        ...g,
        rows: g.rows.sort((a, b) => (a.sequence || 0) - (b.sequence || 0)),
      }))
      .sort((a, b) => (a.sector_name || '').localeCompare(b.sector_name || ''));
  };

  const loadAll = async () => {
    try {
      setLoading(true);
      const [auditRes, reportRes, checklistRes] = await Promise.all([
        auditsApi.get(auditId),
        auditsApi.getReport(auditId),
        auditsApi.listChecklist(auditId),
      ]);
      setAudit(auditRes.data);
      setReport(reportRes.data);
      setEmpresa(reportRes.data?.empresa || '');
      setDistributions(reportRes.data?.distributions || []);
      setAreaGroups(buildAreaGroups(checklistRes.data || []));
    } catch (error) {
      toast.error('Erro ao carregar o relatório da auditoria');
    } finally {
      setLoading(false);
    }
  };

  const updateDistribution = (index, field, value) => {
    setDistributions((prev) =>
      prev.map((d, i) => (i === index ? { ...d, [field]: value } : d))
    );
  };

  const addDistribution = () => {
    setDistributions((prev) => [
      ...prev,
      { id: null, sequence: prev.length + 1, name: '', role: '' },
    ]);
  };

  const removeDistribution = (index) => {
    setDistributions((prev) =>
      prev
        .filter((_, i) => i !== index)
        .map((d, i) => ({ ...d, sequence: i + 1 }))
    );
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        empresa,
        rows: areaGroups.flatMap((g) =>
          g.rows.map((r) => ({
            checklist_id: null,
            sequence: r.sequence,
            requirement: r.requirement,
            description: r.description,
            is_c: r.result === 'conforming',
            is_p: r.result === 'non_conforming',
            is_m: r.result === 'improvement',
          }))
        ),
        distributions: distributions.map((d) => ({
          name: d.name || '',
          role: d.role || '',
        })),
      };
      const res = await auditsApi.saveReport(auditId, payload);
      setReport(res.data);
      setDistributions(res.data.distributions || []);
      toast.success('Relatório salvo!');
    } catch (error) {
      toast.error('Erro ao salvar relatório');
    } finally {
      setSaving(false);
    }
  };

  const handleExportPdf = async () => {
    setExporting(true);
    try {
      const res = await auditsApi.exportReportPdf(auditId);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `relatorio_auditoria_${audit.code}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Erro ao gerar PDF');
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-24">
        <Loader2 className="animate-spin text-green-500" size={32} />
      </div>
    );
  }

  if (!audit) {
    return (
      <div className="text-center py-16 text-gray-400">
        <p>Auditoria não encontrada.</p>
        <button onClick={() => navigate('/auditorias')} className="btn-secondary mt-4">
          <ArrowLeft size={16} className="mr-1" /> Voltar
        </button>
      </div>
    );
  }

  const statusConfig = STATUS_CONFIG[audit.status] || STATUS_CONFIG.planned;
  const typeLabel = AUDIT_TYPES.find((t) => t.value === audit.audit_type)?.label || audit.audit_type;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <Link
            to="/auditorias"
            className="inline-flex items-center gap-1 text-sm text-gray-400 hover:text-green-400 transition-colors mb-2"
          >
            <ArrowLeft size={16} /> Voltar para Auditorias
          </Link>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <FileText size={24} className="text-green-500" /> Relatório de Auditoria
          </h1>
          <div className="flex items-center gap-3 mt-2 flex-wrap">
            <span className="font-mono text-green-400">{audit.code}</span>
            <span className="text-white font-medium">{audit.title}</span>
          </div>
          <div className="flex gap-2 flex-wrap mt-2">
            <span className={`badge ${statusConfig.color}`}>{statusConfig.label}</span>
            <span className="badge badge-gray">{typeLabel}</span>
            {audit.planned_date && (
              <span className="badge badge-gray">
                <Calendar size={12} className="mr-1" />
                {new Date(audit.planned_date).toLocaleDateString('pt-BR')}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleExportPdf}
            disabled={exporting}
            className="btn-secondary flex items-center gap-2"
            title="Exportar Relatório em PDF"
          >
            {exporting ? <Loader2 size={18} className="animate-spin" /> : <Download size={18} />}
            Exportar PDF
          </button>
          <button onClick={handleSave} disabled={saving} className="btn-primary flex items-center gap-2">
            {saving ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />}
            Salvar
          </button>
        </div>
      </div>

      {/* Header data / Empresa */}
      <div className="card p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2 flex items-center gap-1">
              <Building2 size={14} /> Empresa
            </label>
            <input
              type="text"
              className="input w-full"
              placeholder="Nome da empresa auditada"
              value={empresa}
              onChange={(e) => setEmpresa(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Código da Auditoria</label>
            <p className="text-white pt-2 font-mono">{audit.code}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Data</label>
            <p className="text-white pt-2">
              {audit.planned_date ? new Date(audit.planned_date).toLocaleDateString('pt-BR') : '-'}
            </p>
          </div>
        </div>
      </div>

      {/* Constatações por Área Auditada */}
      <div className="space-y-4">
        {areaGroups.length === 0 ? (
          <div className="card p-8 text-center text-gray-400">
            <p>Nenhuma área auditada para este relatório.</p>
            <p className="text-sm mt-1">Adicione setores na agenda da auditoria.</p>
          </div>
        ) : (
          areaGroups.map((group, gi) => (
            <div key={gi} className="card overflow-hidden">
              <div className="px-4 pt-4 pb-3 flex items-center gap-2 flex-wrap">
                <Building2 size={18} className="text-green-400" />
                <p className="text-sm font-medium text-white">
                  {group.sector_name || '-'}
                </p>
                {group.sector_code && (
                  <span className="badge badge-gray">{group.sector_code}</span>
                )}
                <span className="badge badge-gray ml-auto">
                  {group.rows.length} constatação(ões)
                </span>
              </div>
              <div className="table-container">
                <table className="table">
                  <thead>
                    <tr>
                      <th className="w-10">Nº</th>
                      <th>Requisito</th>
                      <th>Item / Verificação</th>
                      <th>Observação (Agenda)</th>
                      <th className="text-center w-16">C</th>
                      <th className="text-center w-16">P</th>
                      <th className="text-center w-16">M</th>
                    </tr>
                  </thead>
                  <tbody>
                    {group.rows.map((row, idx) => {
                      const marks = resultToMarks(row.result);
                      const mark = marks.is_c ? 'C' : marks.is_p ? 'P' : marks.is_m ? 'M' : null;
                      return (
                        <tr key={idx}>
                          <td className="text-gray-500">{row.sequence || idx + 1}</td>
                          <td className="text-gray-400 text-sm">{row.requirement || '-'}</td>
                          <td className="text-white text-sm">{row.description || '-'}</td>
                          <td className="text-gray-400 text-sm whitespace-pre-wrap">
                            {row.observation || '-'}
                          </td>
                          {MARKS.map((m) => (
                            <td key={m.key} className="text-center">
                              <span
                                title={m.title}
                                className={`inline-flex w-8 h-8 rounded border text-sm font-semibold items-center justify-center ${
                                  mark === m.label
                                    ? 'bg-green-600 border-green-500 text-white'
                                    : 'bg-gray-800 border-gray-600 text-gray-400'
                                }`}
                              >
                                {mark === m.label ? m.label : ''}
                              </span>
                            </td>
                          ))}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Lista de Distribuição */}
      <div className="card p-4">
        <div className="flex items-center justify-between mb-3">
          <p className="text-sm font-medium text-gray-300 flex items-center gap-2">
            <Users size={18} /> Lista de Distribuição ({distributions.length})
          </p>
          <button onClick={addDistribution} className="btn-secondary flex items-center gap-1">
            <Plus size={16} /> Adicionar
          </button>
        </div>
        {distributions.length === 0 ? (
          <p className="text-gray-500 text-center py-4 text-sm">Nenhum destinatário. Adicione acima.</p>
        ) : (
          <div className="space-y-2">
            {distributions.map((dist, index) => (
              <div key={index} className="flex flex-col sm:flex-row gap-2 items-center">
                <span className="text-gray-500 text-xs w-6 shrink-0">{dist.sequence || index + 1}</span>
                <input
                  className="input flex-1"
                  placeholder="Nome do destinatário"
                  value={dist.name || ''}
                  onChange={(e) => updateDistribution(index, 'name', e.target.value)}
                />
                <input
                  className="input flex-1"
                  placeholder="Cargo / Função"
                  value={dist.role || ''}
                  onChange={(e) => updateDistribution(index, 'role', e.target.value)}
                />
                <button
                  onClick={() => removeDistribution(index)}
                  className="p-2 text-red-400 hover:text-red-300 transition-colors"
                  title="Remover"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AuditReport;